import { ValueRuleCell } from "./Structure/ValueRuleCell";
import { ValueRuleColumn } from "./Structure/ValueRuleColumn";
import { ValueTableConfiguration } from "./ValueTableConfiguration";
import { IContext } from "../../../Common/IContext";
import { IDesignPanelConfig } from "../../../Common/IDesignPanelConfig";
import { RuleBuilderPage } from "../../RuleBuilderPage";
import { TableColumn } from "../Common/TableColumn";
import { StateManager } from "../../../Common/StateManager";
import { ResultCell } from "../Common/ResultCell";
import { DialogProps } from "../../../Common/DialogProps";
import { AutoBind } from "../../../Common/AutoBind";
import { ILinkingData } from "../../ILinkingData";
import type { AppGlueAny } from "../../../Common/AppGlueAny";

export class ValueTableRuleEditContext implements IContext {
    designConfig?: IDesignPanelConfig | undefined;
    runtimeConfig?: IDesignPanelConfig | undefined;
    clipboardElement: AppGlueAny;
    lastSelectionElement: AppGlueAny;

    mode: RuleBuilderPage = RuleBuilderPage.Rule;
    definition?: ValueTableConfiguration;
    title?: string;
    public onRuleSave?: () => void;
    public onRuleCancel?: () => void;
    _openLinkingDialog: boolean = false;

    constructor(definition?: ValueTableConfiguration) {
        this.definition = definition;
    }

    private _selectedColumnHeader: TableColumn | undefined;

    get selectedColumnHeader() {
        return this._selectedColumnHeader;
    }

    set selectedColumnHeader(col: TableColumn | undefined) {
        this._selectedColumnHeader = col;
        StateManager.propertyChanged(this, "selectedColumnHeader");
    }

    private _selectedColForVariable: ValueRuleColumn | undefined;

    get selectedColForVariable() {
        return this._selectedColForVariable;
    }

    set selectedColForVariable(col: ValueRuleColumn | undefined) {
        this._selectedColForVariable = col;
        StateManager.propertyChanged(this, "selectedColForVariable");
    }

    private _selectedColForRule: ValueRuleColumn | undefined;

    get selectedColForRule() {
        return this._selectedColForRule;
    }

    set selectedColForRule(col: ValueRuleColumn | undefined) {
        this._selectedColForRule = col;
        StateManager.propertyChanged(this, "selectedColForRule");
    }

    private _selectedCellForSettings: ValueRuleCell | undefined;

    get selectedCellForSettings() {
        return this._selectedCellForSettings;
    }

    set selectedCellForSettings(cell: ValueRuleCell | undefined) {
        this._selectedCellForSettings = cell;
        StateManager.propertyChanged(this, "selectedCellForSettings");
    }

    private _selectedCell: ValueRuleCell | ResultCell | undefined;

    get selectedCell() {
        return this._selectedCell;
    }

    set selectedCell(cell: ValueRuleCell | ResultCell | undefined) {
        this._selectedCell = cell;
        StateManager.propertyChanged(this, "selectedCell");
    }

    _notification: DialogProps | undefined;

    get notification() {
        return this._notification;
    }

    set notification(notification: DialogProps | undefined) {
        this._notification = notification;
        StateManager.propertyChanged(this, "notification");
    }

    get openLinkDialog() {
        return this._openLinkingDialog;
    }

    set openLinkDialog(open: boolean) {
        this._openLinkingDialog = open;
        StateManager.propertyChanged(this, "openLinkDialog");
    }

    _linkData: ILinkingData = {
        period: "every_day",
        periodData: {
            period: "day",
            times: 0,
            onWeekDays: [],
            at: "1",
            value: 1,
        },
        storeData: true,
        serviceSetting: false,
        format: "csv",
    };

    get linkData() {
        return this._linkData;
    }

    set linkData(data: ILinkingData) {
        this._linkData = data;
        StateManager.propertyChanged(this, "linkData");
    }

    private _deletingColumn: ValueRuleColumn | undefined;

    get deletingColumn(): ValueRuleColumn | undefined {
        return this._deletingColumn;
    }

    set deletingColumn(column: ValueRuleColumn | undefined) {
        this._deletingColumn = column;
        StateManager.propertyChanged(this, "deletingColumn");
    }

    isSelected(cell: ValueRuleCell | ResultCell | undefined): boolean {
        return this._selectedCell === cell;
    }

    @AutoBind
    onSave() {
        if (this.definition && this.title) {
            this.definition.title = this.title;
        }

        if (this.onRuleSave) {
            this.onRuleSave();
        }
    }

    @AutoBind
    onCancel() {
        this.title = this.definition?.title || "";
        if (this.onRuleCancel) {
            this.onRuleCancel();
        }
    }

    // eslint-disable-next-line
    onCopy(id?: string) {}

    // eslint-disable-next-line
    onCut(id?: string) {}

    // eslint-disable-next-line
    onDelete(id?: string) {}

    // eslint-disable-next-line
    onPaste(id?: string) {}

    navigate = (direction: "Up" | "Down" | "Right" | "Left" | "Tab" | "Select"): void => {
        if (!this.selectedCell) return;

        let newCell: ValueRuleCell | ResultCell | null | undefined = null;
        if (direction === "Left") {
            newCell = this.definition?.getPrevCell(this.selectedCell);
        } else if (direction === "Right" || direction === "Tab") {
            newCell = this.definition?.getNextCell(this.selectedCell);
        } else if (direction === "Down") {
            newCell = this.definition?.getNextRowCell(this.selectedCell);
        } else if (direction === "Up") {
            newCell = this.definition?.getPrevRowCell(this.selectedCell);
        }
        if (newCell) {
            this.selectedCell = newCell;
        }
    };

    setSelected(cell: ResultCell | ValueRuleCell | undefined) {
        this.selectedCell = cell;
    }
}
